/**
 * The MPL stat taxonomy.
 *
 * This is the single source of truth for every per-game stat the league tracks.
 * It mirrors the columns in the Season 1 "Madden Pro League Schedule" workbook
 * (the IAN/Carter/OWEN/MICHEL/Kevin STATS sheets) exactly, so historical data
 * imports 1:1 and future seasons stay comparable.
 *
 * It drives, in order:
 *   - the `game_stats` table columns (server/src/db/schema.ts)
 *   - the Zod validator for stat submission
 *   - the layout + labels of the stat entry form (web)
 *   - career / season / game-log aggregation and the leaderboards
 *
 * A note on what a "player" is here: MPL managers each drive a whole NFL team,
 * so a stat line is one manager's team for one game, bucketed by position group
 * (their QB, their RBs, their WRs...) rather than by individual NFL athlete.
 * That is how the workbook records it and how the league talks about it.
 */
const i = (key, label, full, extra = {}) => ({ key, label, full, kind: "int", ...extra });
export const STAT_GROUPS = [
    {
        key: "situational",
        label: "Team & Situational",
        sheetSection: "COACHING",
        stats: [
            i("third_down_att", "3rd Down Tries", "Third down attempts"),
            i("third_down_conv", "3rd Down Conv", "Third down conversions"),
            i("fourth_down_att", "4th Down Tries", "Fourth down attempts"),
            i("fourth_down_conv", "4th Down Conv", "Fourth down conversions"),
            i("two_pt_att", "2 pt Tries", "Two point conversion attempts"),
            i("two_pt_conv", "2 pt Conv", "Two point conversions"),
            i("fumbles_lost", "Fumbles Lost", "Fumbles lost"),
            // Last in the group deliberately: it is the one field here typed as
            // mm:ss rather than a count, and it is the one people look up last when
            // reading a box score off the screen.
            {
                key: "possession_seconds",
                label: "Possess. Time",
                full: "Time of possession",
                kind: "duration",
            },
        ],
    },
    {
        key: "passing",
        label: "Passing",
        sheetSection: "QB",
        stats: [
            i("pass_cmp", "CMP", "Completions"),
            i("pass_att", "ATT", "Pass attempts"),
            i("pass_yds", "YDS", "Passing yards", { negative: true }),
            i("pass_td", "TD", "Passing touchdowns"),
            i("pass_int", "INT", "Interceptions thrown"),
            {
                key: "sacks_taken",
                label: "SK",
                full: "Sacks taken",
                kind: "half",
            },
        ],
    },
    {
        key: "qb_rushing",
        label: "QB Rushing",
        sheetSection: "QB",
        stats: [
            i("qb_rush_att", "ATT", "QB rush attempts"),
            i("qb_rush_yds", "YDS", "QB rushing yards", { negative: true }),
            i("qb_rush_td", "TD", "QB rushing touchdowns"),
        ],
    },
    {
        key: "rb_rushing",
        label: "RB Rushing",
        sheetSection: "RB",
        stats: [
            i("rb_rush_att", "ATT", "RB rush attempts"),
            i("rb_rush_yds", "YDS", "RB rushing yards", { negative: true }),
            i("rb_rush_td", "TD", "RB rushing touchdowns"),
        ],
    },
    {
        key: "rb_receiving",
        label: "RB Receiving",
        sheetSection: "RB",
        stats: [
            i("rb_rec", "REC", "RB receptions"),
            i("rb_rec_yds", "YDS", "RB receiving yards", { negative: true }),
            i("rb_rec_td", "TD", "RB receiving touchdowns"),
        ],
    },
    {
        key: "wr_receiving",
        label: "WR Receiving",
        sheetSection: "WR",
        stats: [
            i("wr_rec", "REC", "WR receptions"),
            i("wr_rec_yds", "YDS", "WR receiving yards", { negative: true }),
            i("wr_rec_td", "TD", "WR receiving touchdowns"),
        ],
    },
    {
        key: "te_receiving",
        label: "TE Receiving",
        sheetSection: "TE",
        stats: [
            i("te_rec", "REC", "TE receptions"),
            i("te_rec_yds", "YDS", "TE receiving yards", { negative: true }),
            i("te_rec_td", "TD", "TE receiving touchdowns"),
        ],
    },
    {
        key: "defense",
        label: "Defense",
        sheetSection: "DEF",
        stats: [
            i("def_tfl", "TFL", "Tackles for loss"),
            {
                key: "def_sacks",
                label: "SACK",
                full: "Sacks",
                kind: "half",
            },
            i("def_int", "INT", "Interceptions"),
            i("def_int_yds", "INT Yards", "Interception return yards", {
                negative: true,
            }),
            i("def_safeties", "Safeties", "Safeties"),
            i("def_td", "TD", "Defensive touchdowns"),
            i("def_ff", "FF", "Forced fumbles"),
        ],
    },
    {
        key: "kicking",
        label: "Kicking",
        sheetSection: "K",
        stats: [
            i("fga", "FGA", "Field goals attempted"),
            i("fgm", "FGM", "Field goals made"),
            i("fg_long", "FG LONG", "Longest field goal made", {
                advanced: true,
                agg: "max",
            }),
            i("xpa", "XPA", "Extra points attempted"),
            i("xpm", "XPM", "Extra points made"),
        ],
    },
    // Kick and punt returns are separate groups, not one "Returns" block. As a
    // single group the eight rows read "KR, YDS, LONG, TD, PR, YDS, LONG, TD",
    // with three labels repeated and nothing on screen saying which kind of
    // return a row belongs to.
    {
        key: "kick_returns",
        label: "Kick returns",
        sheetSection: "RET",
        stats: [
            i("kr", "KR", "Kick returns", { advanced: true }),
            i("kr_yds", "KR YDS", "Kick return yards", { advanced: true }),
            i("kr_long", "KR LONG", "Longest kick return", {
                advanced: true,
                agg: "max",
            }),
            i("kr_td", "KR TD", "Kick return touchdowns", { advanced: true }),
        ],
    },
    {
        key: "punt_returns",
        label: "Punt returns",
        sheetSection: "RET",
        stats: [
            i("pr", "PR", "Punt returns", { advanced: true }),
            i("pr_yds", "PR YDS", "Punt return yards", { advanced: true }),
            i("pr_long", "PR LONG", "Longest punt return", {
                advanced: true,
                agg: "max",
            }),
            i("pr_td", "PR TD", "Punt return touchdowns", { advanced: true }),
        ],
    },
];
export const ALL_STATS = STAT_GROUPS.flatMap((g) => g.stats);
export const STAT_KEYS = ALL_STATS.map((s) => s.key);
export const STAT_BY_KEY = Object.fromEntries(ALL_STATS.map((s) => [s.key, s]));
/** Stats rolled up with max rather than sum — the "longest" ones. */
export const MAX_STATS = new Set(ALL_STATS.filter((s) => s.agg === "max").map((s) => s.key));
/**
 * Stats that describe the same events as one of the opponent's defensive stats.
 * Sacks you took are sacks their defence made; interceptions you threw are
 * interceptions their defence caught. Recording both sides independently gives
 * two sources of truth for one event, and nothing reconciles them — so the
 * defensive side is authoritative and these are filled in from it.
 */
export const DERIVED_FROM_OPPONENT = {
    sacks_taken: "def_sacks",
    pass_int: "def_int",
};
/**
 * Fill a manager's line from their opponent's defensive line, without
 * overwriting anything already recorded. Leaving directly-recorded values alone
 * is what keeps imported history — where both sides were typed from the
 * workbook — exactly as the workbook has it.
 */
export function applyOpponentDerived(own, opponent) {
    if (!opponent)
        return own;
    const out = { ...own };
    for (const [key, source] of Object.entries(DERIVED_FROM_OPPONENT)) {
        if (out[key] == null && opponent[source] != null) {
            out[key] = opponent[source];
        }
    }
    return out;
}
const n = (v) => (v == null ? 0 : Number(v));
const rate = (num, den) => den > 0 ? num / den : null;
export function deriveStats(s) {
    const rushAtt = n(s.qb_rush_att) + n(s.rb_rush_att);
    const rushYds = n(s.qb_rush_yds) + n(s.rb_rush_yds);
    const rushTd = n(s.qb_rush_td) + n(s.rb_rush_td);
    const rec = n(s.rb_rec) + n(s.wr_rec) + n(s.te_rec);
    const recYds = n(s.rb_rec_yds) + n(s.wr_rec_yds) + n(s.te_rec_yds);
    const recTd = n(s.rb_rec_td) + n(s.wr_rec_td) + n(s.te_rec_td);
    return {
        cmpPct: rate(n(s.pass_cmp), n(s.pass_att)),
        passYpa: rate(n(s.pass_yds), n(s.pass_att)),
        rushAtt,
        rushYds,
        rushTd,
        rushAvg: rate(rushYds, rushAtt),
        rec,
        recYds,
        recTd,
        recAvg: rate(recYds, rec),
        scrimmageYds: rushYds + recYds,
        totalYds: n(s.pass_yds) + rushYds,
        totalTd: n(s.pass_td) + rushTd + n(s.def_td) + n(s.kr_td) + n(s.pr_td),
        offenseTd: n(s.pass_td) + rushTd,
        plays: n(s.pass_att) + rushAtt,
        yardsPerPlay: rate(n(s.pass_yds) + rushYds, n(s.pass_att) + rushAtt),
        intRate: rate(n(s.pass_int), n(s.pass_att)),
        thirdDownPct: rate(n(s.third_down_conv), n(s.third_down_att)),
        fourthDownPct: rate(n(s.fourth_down_conv), n(s.fourth_down_att)),
        fgPct: rate(n(s.fgm), n(s.fga)),
        xpPct: rate(n(s.xpm), n(s.xpa)),
        turnoversCommitted: n(s.pass_int) + n(s.fumbles_lost),
        possessionSeconds: n(s.possession_seconds),
    };
}
/** Roll many stat lines into one, summing (or maxing) each tracked stat.
 * A stat stays null if no contributing line recorded it, so "never tracked"
 * stays visibly distinct from "tracked, and it was zero". */
export function sumStatLines(lines) {
    const out = {};
    for (const key of STAT_KEYS) {
        let acc = null;
        for (const line of lines) {
            const v = line?.[key];
            if (v == null)
                continue;
            const num = Number(v);
            if (Number.isNaN(num))
                continue;
            acc = acc == null ? num : MAX_STATS.has(key) ? Math.max(acc, num) : acc + num;
        }
        out[key] = acc;
    }
    return out;
}
/**
 * Divide a rolled-up line by a game count, for per-game averages.
 *
 * Max stats are passed through untouched: "longest field goal per game" is not
 * a quantity, and dividing it just makes the record look small.
 */
export function averageStatLine(total, games) {
    if (games <= 0)
        return {};
    const out = {};
    for (const key of STAT_KEYS) {
        const v = total[key];
        if (v == null) {
            out[key] = null;
        }
        else if (MAX_STATS.has(key)) {
            out[key] = Number(v);
        }
        else {
            out[key] = Number(v) / games;
        }
    }
    return out;
}
// ---------------------------------------------------------------------------
// Formatting helpers shared by server and web
// ---------------------------------------------------------------------------
/** "15:11" / "1:02:34" — the workbook stores possession time as a duration. */
export function formatDuration(seconds) {
    if (seconds == null || Number.isNaN(seconds))
        return "—";
    const total = Math.round(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
    return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}
/** Accepts "15:11", "15:11:00", "911" (seconds) or "18:06:00" as the workbook
 * writes it. Madden clocks are mm:ss; a trailing ":00" is Excel's doing. */
export function parseDuration(input) {
    if (input == null || input === "")
        return null;
    if (typeof input === "number")
        return Math.round(input);
    const raw = input.trim();
    if (!raw)
        return null;
    if (/^\d+$/.test(raw))
        return Number(raw);
    const parts = raw.split(":").map((p) => p.trim());
    if (parts.some((p) => p === "" || !/^\d+$/.test(p)))
        return null;
    const nums = parts.map(Number);
    if (nums.length === 2)
        return nums[0] * 60 + nums[1];
    if (nums.length === 3) {
        // "18:06:00" from the sheet means 18 minutes 6 seconds, not 18 hours.
        if (nums[2] === 0 && nums[0] < 60)
            return nums[0] * 60 + nums[1];
        return nums[0] * 3600 + nums[1] * 60 + nums[2];
    }
    return null;
}
export function formatPct(v, digits = 1) {
    if (v == null || Number.isNaN(v))
        return "—";
    return `${(v * 100).toFixed(digits)}%`;
}
export function formatAvg(v, digits = 1) {
    if (v == null || Number.isNaN(v))
        return "—";
    return v.toFixed(digits);
}
export function formatStatValue(key, v) {
    if (v == null || Number.isNaN(v))
        return "—";
    const def = STAT_BY_KEY[key];
    if (def?.kind === "duration")
        return formatDuration(v);
    const num = Number(v);
    if (Number.isInteger(num))
        return String(num);
    return num.toFixed(def?.kind === "half" ? 1 : 1);
}
//# sourceMappingURL=stats.js.map